import { useScrollReveal } from '../hooks/useScrollReveal';

const fourLayers = [
  {
    layer: '第一层',
    title: '课堂原话',
    desc: '提取完整的语音转写内容，还原师生对话原貌',
    icon: 'ri-mic-line',
    badge: '话语识别',
    color: 'primary',
  },
  {
    layer: '第二层',
    title: '时间戳',
    desc: '精准定位关键语句在第几分第几秒出现',
    icon: 'ri-timer-line',
    badge: '精确定位',
    color: 'accent',
  },
  {
    layer: '第三层',
    title: '视频片段',
    desc: '把语句放回当时的课件与课堂活动中还原情境',
    icon: 'ri-video-line',
    badge: '情境还原',
    color: 'primary',
  },
  {
    layer: '第四层',
    title: '评价判断',
    desc: '专业判定支撑的问题设计、理论阐释与互动质量',
    icon: 'ri-check-double-line',
    badge: '专业判定',
    color: 'accent',
  },
];

const bottomTags = [
  { label: '可学习', icon: 'ri-book-open-line', color: 'primary' as const },
  { label: '可验证', icon: 'ri-shield-check-line', color: 'accent' as const },
  { label: '可追溯', icon: 'ri-route-line', color: 'primary' as const },
];

export default function P15EvidenceChain({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);

  return (
    <div className="relative min-h-[calc(100vh-80px)] flex flex-col justify-center overflow-hidden bg-background-50">
      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 lg:px-12 py-8 md:py-10">
        {/* 标题区 - 高度压缩 */}
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50/80 border border-primary-200/40 text-[11px] font-body font-semibold text-primary-600 tracking-wider mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <i className="ri-link-m text-xs" />
            证据体系
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            证据链的<span className="text-primary-600">四层结构</span>
          </h2>
          <p className={`text-xs md:text-sm text-foreground-500 font-body max-w-sm mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            四层连起来，评价结果就有了来处
          </p>
        </div>

        {/* 横向四列卡片流 */}
        <div className={`relative transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* 背景连接线 */}
          <div className="hidden lg:block absolute top-[72px] left-[12.5%] right-[12.5%] h-0.5"
            style={{
              background: 'linear-gradient(to right, oklch(var(--primary-300) / 0.35), oklch(var(--accent-300) / 0.35), oklch(var(--primary-400) / 0.35), oklch(var(--accent-400) / 0.35))',
            }}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {fourLayers.map((layer, i) => {
              const isPrimary = layer.color === 'primary';
              return (
                <div
                  key={layer.layer}
                  className="relative group"
                  style={{ transitionDelay: `${300 + i * 120}ms` }}
                >
                  {/* 连接箭头（桌面端卡片之间） */}
                  {i < 3 && (
                    <div className="hidden lg:flex absolute -right-3 top-[60px] z-20 items-center justify-center w-6 h-6 rounded-full bg-background-50 border border-background-200/60">
                      <i className="ri-arrow-right-line text-[10px] text-foreground-400" />
                    </div>
                  )}

                  <div
                    className={`relative rounded-xl border overflow-hidden transition-all duration-500 hover:-translate-y-1 card-premium-striped ${
                      isPrimary
                        ? 'border-primary-200/50 hover:border-primary-300/70'
                        : 'border-accent-200/50 hover:border-accent-300/70'
                    }`}
                    style={{
                      background: isPrimary
                        ? 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(245,250,255,0.7) 100%)'
                        : 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,251,245,0.7) 100%)',
                    }}
                  >
                    {/* 顶部色条 */}
                    <div className="h-1 w-full" style={{ background: isPrimary ? 'oklch(var(--primary-400))' : 'oklch(var(--accent-400))', opacity: 0.7 }} />

                    <div className="p-4 md:p-5">
                      {/* 头部：编号 + 层级 */}
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: isPrimary ? 'oklch(var(--primary-50))' : 'oklch(var(--accent-50))', border: `1px solid ${isPrimary ? 'oklch(var(--primary-200) / 0.4)' : 'oklch(var(--accent-200) / 0.4)'}` }}
                        >
                          <span className={`font-mono text-sm font-bold ${isPrimary ? 'text-primary-600' : 'text-accent-600'}`}>
                            {String(i + 1).padStart(2, '0')}
                          </span>
                        </div>
                        <span className="text-[11px] font-body text-foreground-400 tracking-wider">
                          {layer.layer}
                        </span>
                      </div>

                      {/* 图标 + 标题 */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isPrimary ? 'bg-primary-50' : 'bg-accent-50'}`}>
                          <i className={`${layer.icon} text-base ${isPrimary ? 'text-primary-600' : 'text-accent-600'}`} />
                        </div>
                        <h4 className={`font-heading text-base md:text-lg font-bold ${isPrimary ? 'text-primary-700' : 'text-accent-700'}`}>
                          {layer.title}
                        </h4>
                      </div>

                      {/* 描述 */}
                      <p className="text-xs text-foreground-500 font-body leading-relaxed mb-3 min-h-[40px]">
                        {layer.desc}
                      </p>

                      {/* Badge */}
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-body font-semibold ${
                        isPrimary
                          ? 'bg-primary-50 text-primary-700 border border-primary-200/40'
                          : 'bg-accent-50 text-accent-700 border border-accent-200/40'
                      }`}>
                        <i className={`${isPrimary ? 'ri-shield-check-line' : 'ri-verified-badge-line'} text-[10px]`} />
                        {layer.badge}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 底部标签栏 - 紧凑 */}
        <div className={`mt-6 md:mt-8 max-w-md mx-auto transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-1.5 md:gap-2 p-3 md:p-4 rounded-xl border border-background-200/60"
            style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,252,0.5) 100%)' }}
          >
            {bottomTags.map((tag, i) => {
              const isAccent = tag.color === 'accent';
              return (
                <div key={tag.label} className="flex items-center">
                  <div className={`flex items-center gap-1.5 px-2.5 py-1.5 md:px-3 md:py-2 rounded-lg ${
                    isAccent
                      ? 'bg-accent-50/80 border border-accent-200/40'
                      : 'bg-primary-50/80 border border-primary-200/40'
                  }`}
                  >
                    <i className={`${tag.icon} text-[10px] ${isAccent ? 'text-accent-500' : 'text-primary-500'}`} />
                    <span className={`text-[11px] md:text-xs font-body font-semibold ${isAccent ? 'text-accent-700' : 'text-primary-700'}`}>
                      {tag.label}
                    </span>
                  </div>
                  {i < 2 && (
                    <i className="ri-arrow-right-line text-foreground-300 text-[10px] mx-1" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}